interface Props {
  publicSlug: string | null;
  status: string;
}

export default function PortalTab({ publicSlug, status }: Props) {
  const publicUrl = publicSlug ? `${window.location.origin}/guestbook/${publicSlug}` : null;

  return (
    <div className="space-y-5 max-w-xl">
      {/* Status banner */}
      {status === "published" ? (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 shrink-0">
            <i className="ri-checkbox-circle-line text-lg"></i>
          </div>
          <div>
            <p className="text-emerald-800 font-semibold text-sm">Libro publicado y activo</p>
            <p className="text-emerald-600 text-xs mt-0.5">Los invitados pueden acceder con el link público</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-100 text-amber-600 shrink-0">
            <i className="ri-draft-line text-lg"></i>
          </div>
          <div>
            <p className="text-amber-800 font-semibold text-sm">
              {status === "archived" ? "Libro archivado" : "Libro en borrador"}
            </p>
            <p className="text-amber-600 text-xs mt-0.5">
              {status === "archived"
                ? "Este libro ya no acepta nuevas visitas"
                : "Cambia el estado a \"Publicado\" para que los invitados puedan acceder"}
            </p>
          </div>
        </div>
      )}

      {/* Public link */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-slate-700 font-semibold text-sm mb-3 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-amber-500"><i className="ri-link-m"></i></span>
          Link público
        </h3>
        {publicUrl ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl border border-slate-200 px-4 py-3">
              <i className="ri-link-m text-slate-400 text-sm shrink-0"></i>
              <span className="flex-1 text-sm text-slate-600 truncate">{publicUrl}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Abrir libro
              </a>
              <button
                onClick={() => { navigator.clipboard.writeText(publicUrl); }}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 hover:text-slate-800 text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-clipboard-line"></i>
                Copiar link
              </button>
            </div>
          </div>
        ) : (
          <p className="text-slate-400 text-sm">Sin slug público asignado.</p>
        )}
      </div>

      {/* What the guest sees */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-slate-700 font-semibold text-sm mb-4 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center text-amber-500"><i className="ri-smartphone-line"></i></span>
          Experiencia del invitado
        </h3>
        <div className="space-y-3">
          {[
            { step: "1", icon: "ri-qr-code-line",     title: "Escanea el QR",          desc: "El invitado escanea el código QR desde su celular" },
            { step: "2", icon: "ri-image-2-line",      title: "Ve las fotos",            desc: "Navega por las fotos del libro como un álbum" },
            { step: "3", icon: "ri-cursor-line",       title: "Toca una foto",           desc: "Selecciona dónde quiere dejar su mensaje" },
            { step: "4", icon: "ri-pencil-line",       title: "Escribe su mensaje",      desc: "Escribe texto, elige emoji y firma" },
            { step: "5", icon: "ri-check-double-line", title: "Mensaje guardado",        desc: "El mensaje queda anclado sobre la foto con su posición exacta" },
          ].map((item) => (
            <div key={item.step} className="flex items-start gap-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700 text-xs font-bold shrink-0">
                {item.step}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <i className={`${item.icon} text-amber-500`}></i>
                  {item.title}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PDF note */}
      <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-200 text-slate-500 shrink-0">
            <i className="ri-file-pdf-2-line text-lg"></i>
          </div>
          <div>
            <p className="text-slate-700 font-semibold text-sm">Exportar a PDF</p>
            <p className="text-slate-400 text-xs">Disponible próximamente</p>
          </div>
        </div>
        <p className="text-slate-400 text-xs leading-relaxed">
          Podrás descargar el libro completo con las fotos y mensajes de los invitados en formato PDF de alta calidad para imprimir o compartir.
        </p>
      </div>
    </div>
  );
}